import { ref } from 'vue';
import { storeToRefs } from 'pinia';
import { z } from 'zod';
import { useRolePermission, useTargetPermissionCheck } from '@/common/permission/permission.utils';
import { useMessage } from '@/common/inject/useMessage';
import { paExceptionHandler } from '@/common/utils/apiUtils';
import { getSlideDetailStore } from '@/common/stores/slide-detail';
import type { SlideDetailAdapterResult } from '@/common/types/slideDetailAdapter.types';
import { createSlideDetailResult } from '@/common/utils/slideDetailAdapterResult';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
import {
  InvalidDetailTypeError,
  useSessionDetail,
  useSqlDetail,
  useParameterDetail,
  useMiscellaneousDetail,
  useDocumentTextWindow,
} from './adapters';
import type {
  DatabaseAdapterParams,
  OpenDetailOptions,
  OpenDetailParams,
  OpenDocumentOptions,
  OpenDocumentParams,
} from './openDetail.types';
import { useInstanceDetail } from './adapters/instanceDetail.uses';
import { DATABASE_DETAIL_TYPE, DOCUMENT_TEXT_TYPE } from './openDetail.define';
import {
  convertDatabaseDetailErrorToSlideDetailAdapterError,
  getErrorMessageFromError,
} from './openDetail.utils';
import { TargetStatusRequestTargetKind } from '@/api-client/target/model';
import { DB_TYPE } from '@/common/define/common.define';
import { getDetailInfo } from './adapters/instanceDetail.utils';
import { useInternational } from '@/common/locale';

/**
 * 대시보드/기본뷰 공통 입력 파라미터 → OpenDetailParams 변환 스키마
 *
 * 대시보드 경로: rawData → infoObj, targetId → instanceId, fromTime+toTime → fixedTimeRange
 * 기본뷰 경로:  infoObj·instanceId·fixedTimeRange 그대로 통과
 */
const databaseDetailParamsSchema = z
  .object({
    type: z.string().optional(),
    instanceId: z.string().optional(),
    targetId: z.string().optional(),
    rawData: z.record(z.string(), z.any()).optional(),
    infoObj: z.record(z.string(), z.any()).optional(),
    dbType: z.string().optional(),
    fromTime: z.string().optional(),
    toTime: z.string().optional(),
    fixedTimeRange: z.object({ fromTime: z.string(), toTime: z.string() }).optional(),
  })
  .transform(
    ({
      instanceId,
      targetId,
      rawData,
      infoObj,
      fromTime,
      toTime,
      fixedTimeRange,
      dbType,
      ...rest
    }): OpenDetailParams => {
      const resolvedFromTime = fromTime || rawData?.fromTime;
      const resolvedToTime = toTime || rawData?.toTime;

      return {
        ...rest,
        dbType: dbType || rawData?.dbType || '',
        instanceId: instanceId || rawData?.instanceId || infoObj?.instanceId || targetId || '',
        infoObj: rawData ?? infoObj,
        fixedTimeRange:
          fixedTimeRange ??
          (resolvedFromTime && resolvedToTime
            ? { fromTime: resolvedFromTime, toTime: resolvedToTime }
            : undefined),
      };
    },
  );

export const useOpenDatabaseDetail = () => {
  const { showMsg } = useMessage();
  const { t } = useInternational();

  const isSlideDetailOpened = ref<boolean>(false);

  const slideDetailStore = getSlideDetailStore();
  const { addTabPanel, addSplitTabPanel } = slideDetailStore;
  const { isOpened } = storeToRefs(slideDetailStore);

  const { isPermissionDenied } = useRolePermission();
  const { checkIsValidTarget } = useTargetPermissionCheck();

  const { getSessionDetailAddition } = useSessionDetail();
  const { getSqlDetailAddition } = useSqlDetail();
  const { getParameterDetailAddition } = useParameterDetail();
  const { getInstanceDetailAddition } = useInstanceDetail();
  const {
    getObjectDetailAddition,
    getSlowQueryAddition,
    getBrokerDetailAddition,
    getTransactionDetailAddition,
    getReplicationDetailAddition,
    getReplicationObjectDetailAddition,
  } = useMiscellaneousDetail();

  const {
    isDocumentWindowOpen,
    isDocumentWindowLoading,
    documentInfo,

    openDocumentWindow: openDocumentWindowWithoutApiCall,
    openDocumentWindowWithApiCall,
  } = useDocumentTextWindow();

  const getAdditionByType = async (
    params: OpenDetailParams,
    sessionOptions?: OpenDetailOptions['sessionOptions'],
  ) => {
    const { type, instanceId, dbType, infoObj = {}, fixedTimeRange } = params;
    const { fromTime, toTime } = fixedTimeRange || {};
    // fixedTimeRange 가 없으면, 연계 시간 기준으로 fromTime, toTime 계산하여 사용
    // fixedTimeRange 가 있으면 그대로 사용하되, 최소 시간기준 (세션 10분, SQL 1시간) 보다 작을 경우 강제로 최소기준에 맞춤

    const { isSessionInit = true, lockType, isOpenedFromPA, isSlowQuery } = sessionOptions || {};

    switch (type) {
      case DATABASE_DETAIL_TYPE.SESSION: {
        return getSessionDetailAddition({
          instanceId: instanceId || infoObj.instanceId || '',
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
          init: isSessionInit,
          lockType,
          isOpenedFromPA,
          isSlowQuery,
        });
      }
      case DATABASE_DETAIL_TYPE.SQL: {
        return getSqlDetailAddition({
          instanceId: instanceId || infoObj.instanceId || '',
          dbType: dbType === 'cluster_singlestore' ? DB_TYPE.SINGLESTORE : dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.PARAMETER: {
        return getParameterDetailAddition({
          instanceId,
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.SLOW_QUERY: {
        return getSlowQueryAddition({
          instanceId: instanceId || infoObj.instanceId || '',
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.OBJECT: {
        return getObjectDetailAddition({
          instanceId: instanceId || infoObj.instanceId || '',
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.TRANSACTION: {
        return getTransactionDetailAddition({
          dbType,
          detailInfo: infoObj,
        });
      }
      case DATABASE_DETAIL_TYPE.REPLICATION: {
        return getReplicationDetailAddition({
          instanceId,
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.REPLICATION_OBJECT: {
        return getReplicationObjectDetailAddition({
          instanceId,
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.BROKER: {
        return getBrokerDetailAddition({
          instanceId,
          dbType,
          detailInfo: infoObj,
          fromTime,
          toTime,
        });
      }
      case DATABASE_DETAIL_TYPE.UNRESOLVED_INSTANCE:
      case DATABASE_DETAIL_TYPE.INSTANCE:
      case DATABASE_DETAIL_TYPE.CLUSTER:
      case DATABASE_DETAIL_TYPE.SHARDED_CLUSTER: {
        const addition = await getInstanceDetailAddition({
          instanceId,
          dbType: dbType === 'cluster_singlestore' ? DB_TYPE.SINGLESTORE : dbType,
          detailInfo: infoObj,
          detailType: type === DATABASE_DETAIL_TYPE.UNRESOLVED_INSTANCE ? undefined : type,
          fromTime,
          toTime,
        });

        return addition;
      }
      default: {
        throw new InvalidDetailTypeError({ detailType: type || '(empty)' }); // unreachable?
      }
    }
  };

  const errorCatcher = (e: unknown, showErrorMessage: boolean) => {
    const msgStr = getErrorMessageFromError(e);

    if (!showErrorMessage) {
      return;
    }

    if (msgStr) {
      showMsg({ type: 'error', msgStr });
    } else {
      paExceptionHandler({ error: e, isShowRejectMsg: true });
    }
  };

  const openDocumentWindow = async (
    params: OpenDocumentParams,
    options?: OpenDocumentOptions,
  ): Promise<SlideDetailAdapterResult> => {
    const { showErrorMessage = true } = options ?? {};

    if ('text' in params) {
      openDocumentWindowWithoutApiCall(params);
      return createSlideDetailResult('ok');
    }

    try {
      await openDocumentWindowWithApiCall(
        {
          detailInfo: params.infoObj || {},
          instanceId: params.instanceId,
          ...(params.fixedTimeRange || { period: 'p5s' }),
          useSampleText: options?.useSampleText ?? false,
          useFieldValidation: options?.useFieldValidation ?? true,
        },
        params.dbType,
      );
      return createSlideDetailResult('ok');
    } catch (e: unknown) {
      errorCatcher(e, showErrorMessage);
      return createSlideDetailResult(convertDatabaseDetailErrorToSlideDetailAdapterError(e));
    }
  };

  const openDatabaseDetail = async (
    params: z.input<typeof databaseDetailParamsSchema>,
    options?: OpenDetailOptions,
  ): Promise<SlideDetailAdapterResult> => {
    const normalized = databaseDetailParamsSchema.parse(params);
    const {
      runCheckPermission = true,
      showErrorMessage = true,
      openMode = 'main',
      keepSplitTab = false,
    } = options?.adapterOptions ?? {};

    const isDatabasePermissionDenied =
      runCheckPermission &&
      isPermissionDenied({
        rolePermissionKey: ROLE_PERMISSION_KEY.MONITORING.MONITORING_DATABASE_INSTANCE,
        type: 'detailSlide',
        showError: showErrorMessage,
      });
    if (isDatabasePermissionDenied) {
      return createSlideDetailResult('permission_denied');
    }

    try {
      const [targetId] = (normalized.instanceId || '').split('|');
      let instanceId = targetId;

      if (!normalized.type || normalized.type === DATABASE_DETAIL_TYPE.UNRESOLVED_INSTANCE) {
        const detailInfo = await getDetailInfo(targetId);

        if (!detailInfo.instanceId) {
          if (showErrorMessage) {
            showMsg({ type: 'error', msgStr: t('MESSAGE.LINK_NOT_AVAILABLE_TARGET') });
          }
          return createSlideDetailResult('invalid_params');
        }

        instanceId = detailInfo.instanceId;
        normalized.instanceId = detailInfo.instanceId;
        normalized.type = detailInfo.detailType;
        normalized.dbType = detailInfo.dbType;
      }

      const targetValidation = await checkIsValidTarget(
        { targetId: instanceId, targetKind: TargetStatusRequestTargetKind.DATABASE },
        { showErrorMessage },
      );

      if (!targetValidation.valid) {
        return createSlideDetailResult(targetValidation.reason);
      }

      const addition = await getAdditionByType(normalized, options?.sessionOptions);
      const { init = true, state, currentState } = options ?? {};

      const tabPanelInfo = {
        ...addition,
        init,
        state,
        keepSplitTab: openMode === 'split' ? keepSplitTab : undefined,
      };

      if (openMode === 'split') {
        addSplitTabPanel(tabPanelInfo);
        isSlideDetailOpened.value = isOpened.value;
        return createSlideDetailResult('ok');
      }

      const result = addTabPanel(tabPanelInfo, currentState);

      isSlideDetailOpened.value = isOpened.value;
      return createSlideDetailResult(result);
    } catch (e: unknown) {
      errorCatcher(e, showErrorMessage);
      return createSlideDetailResult(convertDatabaseDetailErrorToSlideDetailAdapterError(e));
    }
  };

  const openDatabaseDetailDashboard = async ({
    params,
    options,
    currentState,
    init,
    selectedTab,
    state,
  }: DatabaseAdapterParams): Promise<SlideDetailAdapterResult> => {
    const openDetailOptions: OpenDetailOptions = {
      init,
      state: {
        ...state,
        selectedTab,
      },
      currentState,
      adapterOptions: options,
      sessionOptions: {
        isSessionInit: params.isSessionInit,
        lockType: params.lockType,
        isOpenedFromPA: params.isOpenedFromPA,
      },
    };

    //  Document Window 연계시 (Cell click 등)
    if (params.type === DOCUMENT_TEXT_TYPE) {
      const normalized = databaseDetailParamsSchema.parse(params);
      const fixedTimeRange = normalized.fixedTimeRange || { period: params.period || 'p5s' };
      const result = await openDocumentWindow(
        { ...normalized, fixedTimeRange },
        { showErrorMessage: options?.showErrorMessage },
      );
      return result;
    }

    return openDatabaseDetail(params, openDetailOptions);
  };

  return {
    documentInfo,
    isDocumentWindowLoading,
    isDocumentWindowOpen,

    isSlideDetailOpened,

    openDocumentWindow,
    openDatabaseDetail,
    openDatabaseDetailDashboard,
  };
};
